import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff } from 'lucide-react';  // FEATURE 2: Show password icons

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [screenname, setScreenname] = useState('');
  const [showPassword, setShowPassword] = useState(false);  // FEATURE 2: Toggle state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setEmailError(false);

    try {
      await register(email, password, screenname || null);
      toast({
        title: 'Welcome to MyIniraa!',
        description: 'Your account has been created successfully.',
      });
      navigate('/myiniraa');
    } catch (error) {
      // Enhanced error feedback
      const errorDetail = error.response?.data?.detail || '';
      
      if (error.response?.status === 400 && errorDetail.toLowerCase().includes('already exists')) {
        setError('Account already exists with this email.');
        setEmailError(true);
      } else if (errorDetail) {
        setError(errorDetail);
      } else {
        setError('Unable to create account. Please try again.');
      }
      
      toast({
        title: 'Registration failed',
        description: error,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9F9F9] to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#7B3F9E] mb-2">Join MyIniraa</h1>
          <p className="text-gray-600">Create your account to connect with the community</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(false);
                setError('');
              }}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition ${
                emailError ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="your@email.com"
              data-testid="register-email-input"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition"
                placeholder="••••••••"
                data-testid="register-password-input"
              />
              {/* FEATURE 2: Show/Hide Password Button */}
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="screenname" className="block text-sm font-medium text-gray-700 mb-2">
              Screenname (Optional)
            </label>
            <input
              id="screenname"
              type="text"
              value={screenname}
              onChange={(e) => setScreenname(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition"
              placeholder="e.g., WellnessWarrior, HealthyJourney"
              data-testid="register-screenname-input"
            />
            <p className="text-xs text-gray-500 mt-1">This is how you'll appear in the community. A creative wellness-themed name will be suggested if left empty (like "WellnessWarrior" or "HealingPath").</p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#FF8C00] text-white py-3 rounded-lg font-semibold hover:bg-[#E67E00] transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="register-submit-button"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[#FF8C00] font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
